/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect } from 'react';

// Mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';

// Mantine form
import {
	useForm,
	isNotEmpty,
	matches,
} from '@mantine/form';

// Styles
import classes from './UpdateMeasurabilityProject.module.css';

// Components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import {
	REGEX_LINKEDIN,
	REGEX_VALID_NAME,
	REGEX_VALID_PARAGRAPHE_RICH_TEXT,
	REGEX_VALID_PLACE,
	REGEX_VALID_RICH_TEXT,
	REGEX_WEBSITE,
	REGEX_X,
} from '@config/constants/regex';

// Router
import { useParams } from 'react-router';

import { UpdateMeasurabilityProjectForm } from './UpdateMeasurabilityProjectForm';
import { useMeasurabilityProject } from '@features/catalogs/meausrability-projects/list/api/get-measurability-project-by-id-update';
import {
	LINKEDIN_URL,
	X_URL,
} from '@config/constants/common';
import { useMeasurabilityProjectUpdate } from '@features/catalogs/meausrability-projects/list/hooks/useMeasurabilityProjectUpdate';
import { formatTextWithHTMLParagraph } from '@utils/formats';
import isNil from 'lodash/isNil';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
type TUpdateMeasurabilityProjectProps = {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
};
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const UpdateMeasurabilityProject = (
	props: TUpdateMeasurabilityProjectProps
) => {
	const { projectId } = useParams();

	const { data, isFetching } = useMeasurabilityProject({
		projectId: projectId ? projectId : '',
		queryConfig: {
			refetchOnMount: true,
			refetchOnReconnect: true,
			retryOnMount: true,
		},
	});

	/* ******************************** HOOKS *********************************** */

	const { t } = useTranslation();
	/* ******************************** Zod schema  *********************************** */
	const form = useForm({
		initialValues: {
			entity: '',
			supportedBy: '',
			name: '',
			//unit: '',
			sellPrice_europertco2eq: 0,
			costPrice_europertco2eq: 0,
			countryCode: '',
			place: '',
			cover: null,
			logo: null,
			unit: null,
			technology: '',
			technicalDocuments: null,
			internalTechnicalDocuments: null,
			twitter: '',
			linkedin: '',
			website: '',
			description: '<p></p>',
			active: true,
		},

		validate: {
			entity: isNotEmpty(t('global.entityRequired')),
			name: (value) => {
				if (value.length == 0) {
					return t('global.nameRequired');
				}
				if (value.length == 1) {
					return t('form.error.invalidNameLength');
				}
				if (
					!REGEX_VALID_NAME.test(value) ||
					value.length > 40
				) {
					return t('form.error.invalidName');
				}
				return null;
			},

			countryCode: isNotEmpty(
				t('global.countryRequired')
			),

			place: (value) => {
				if (value.length == 0) {
					return t('global.placeRequired');
				}
				if (value.length == 1) {
					return t('form.error.invalidPlaceLength');
				}
				if (
					!REGEX_VALID_PLACE.test(value) ||
					value.length > 50
				) {
					return t('form.error.invalidPlace');
				}
				return null;
			},
			unit: isNotEmpty(t('global.unitRequired')),
			technology: isNotEmpty(
				t('global.technologyRequired')
			),
			cover: isNotEmpty(t('global.coverRequired')),
			twitter: matches(REGEX_X, 'form.error.invalidX'),
			website: matches(
				REGEX_WEBSITE,
				'form.error.invalidWebsite'
			),
			linkedin: matches(
				REGEX_LINKEDIN,
				'form.error.invalidLinkedin'
			),
			costPrice_europertco2eq: (value) =>
				value < 0 ? t('global.pricePositive') : null,
			sellPrice_europertco2eq: (value) =>
				value <= 0 ? t('global.pricePositive') : null,
			description: (value) => {
				if (!REGEX_VALID_RICH_TEXT.test(value)) {
					return t('global.descriptionRequired');
				}
				if (
					!REGEX_VALID_PARAGRAPHE_RICH_TEXT.test(value)
				) {
					return t('form.error.invalidText');
				}
				return null;
			},
		},
	});
	const handleclosedrawer = () => {
		props.onClose();
		form.reset();
	};

	const {
		viewport,
		isOpenModalCancel,
		setIsOpenModalCancel,
		handleCloseDrawerAddMeasurabilityProject,
		setInitialMeasurabilityData,
	} = useMeasurabilityProjectUpdate({
		form: form,
		onBack: () => handleclosedrawer,
	});

	useEffect(() => {
		if (!isFetching && data) {
			if (!isNil(data)) {
				setInitialMeasurabilityData(data);
			}
			console.log('data ', data);
			form.initialize({
				name: data.name,
				entity: data?.entity?.id,
				countryCode: data?.location?.countryCode,
				place: data?.location?.place,
				unit: data.unit,
				cover: data.cover,
				logo: data.logo,
				technology: data.technology,
				costPrice_europertco2eq:
					data.costPrice_europertco2eq,
				sellPrice_europertco2eq:
					data.sellPrice_europertco2eq,
				description: data.description
					? formatTextWithHTMLParagraph(data.description)
					: '',
				technicalDocuments: null,
				internalTechnicalDocuments: null,
				twitter: data.social?.twitter
					? X_URL + '/' + data.social.twitter
					: '',
				linkedin: data.social?.linkedIn
					? LINKEDIN_URL + '/' + data.social.linkedIn
					: '',
				active: data.active,
				website: data.social?.website
					? data.social?.website
					: '',
				supportedBy: data?.supportedBy || null,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFetching]);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={async () => {
						await handleCloseDrawerAddMeasurabilityProject(
							form,
							data
						);
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={t('global.backToProjectDetailsSure')}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t('global.backToProjectDetails')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<UpdateMeasurabilityProjectForm
								dataApi={data}
								isPreviousMeasurabilityProjectDetails={
									true
								}
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={handleclosedrawer}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />
					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
